import { SEAT_CATEGORIES } from '@src/components/sco/Advisors/data/seatCategories'
import {
  SEAT_COMMITMENTS,
  SEAT_COMPENSATION_TYPES,
  SeatCommitmentOption,
} from '@src/components/sco/Advisors/data/seatIndustries'
import { SeatData } from '@src/components/sco/Advisors/types'
import { CATEGORY_KEY_TO_NAME } from '@src/components/sco/Advisors/utils/categoryKeys'
import {
  ADVISOR_ENGAGEMENT_STATUS_ACTIVE,
  ADVISOR_ENGAGEMENT_STATUS_INVITED,
  ADVISOR_ENGAGEMENT_STATUS_SEARCHING,
} from '@src/constants/advisor-engagement-statuses'
import { ScoAdvisorEngagement } from '@src/graphql-types'

export const labelsForSeatKeys = (
  keys: Array<string | null | undefined> | null | undefined,
  options: SeatCommitmentOption[]
): string[] => {
  const out: string[] = []
  for (const key of keys ?? []) {
    if (!key) {
      continue
    }
    out.push(options.find(option => option.key === key)?.label ?? key)
  }
  return out
}

export const seatHasAdvisor = (
  engagement: Pick<ScoAdvisorEngagement, 'advisorUser' | 'advisorUserId' | 'advisorName' | 'isAI'>
): boolean =>
  !!(engagement.advisorUser || engagement.advisorUserId || engagement.advisorName || engagement.isAI)

export const seatAdvisorDisplayName = (engagement: ScoAdvisorEngagement): string | null =>
  engagement.advisorUser?.fullName || engagement.advisorUser?.displayName || engagement.advisorName || null

export const seatAdvisorBio = (engagement: ScoAdvisorEngagement): string | null => {
  const user = engagement.advisorUser
  const profile = user?.advisorProfile
  const bio = profile?.headline || user?.shortBio || profile?.bioForAdvising || user?.bio || null
  const trimmed = bio?.trim()
  return trimmed || null
}

export const buildSeatTitle = (engagement: ScoAdvisorEngagement, categoryName: string): string => {
  const requestTitle = engagement.groupRequest?.title?.trim()
  if (requestTitle) {
    return requestTitle
  }
  return categoryName ? `${categoryName} advisor` : 'Board seat'
}

export const seatStatusPalette = (statusKey?: string | null): string => {
  switch (statusKey) {
    case ADVISOR_ENGAGEMENT_STATUS_ACTIVE:
      return 'green'
    case ADVISOR_ENGAGEMENT_STATUS_INVITED:
      return 'orange'
    case ADVISOR_ENGAGEMENT_STATUS_SEARCHING:
      return 'blue'
    default:
      return 'gray'
  }
}

export const engagementToSeatData = (engagement: ScoAdvisorEngagement): SeatData => {
  const categoryKey = engagement.categoryKey || engagement.category?.key || ''
  const seatCategory =
    SEAT_CATEGORIES.find(c => c.key === categoryKey) ??
    SEAT_CATEGORIES.find(
      c => c.name === (engagement.category?.title || CATEGORY_KEY_TO_NAME[categoryKey] || '')
    ) ??
    SEAT_CATEGORIES[0]
  const categoryName =
    engagement.category?.title || CATEGORY_KEY_TO_NAME[categoryKey] || seatCategory.name || 'Board seat'
  const candidateCount = engagement.candidateCount ?? 0
  const advisorName = seatAdvisorDisplayName(engagement)

  return {
    category: categoryName,
    palette: seatCategory.palette,
    icon: seatCategory.icon,
    industries: (engagement.industryKeys ?? []).filter((value): value is string => !!value),
    skills: (engagement.skillTagTitles ?? []).filter((value): value is string => !!value),
    why: engagement.whyText || seatCategory.role,
    status: engagement.status?.title || engagement.statusKey || 'Draft',
    candidates: candidateCount === 1 ? '1 application' : `${candidateCount} applications`,
    candidateCount,
    title: buildSeatTitle(engagement, categoryName),
    companyName: engagement.company?.name || null,
    compensationLabels: labelsForSeatKeys(engagement.compensationTypeKeys, SEAT_COMPENSATION_TYPES),
    cadenceLabels: labelsForSeatKeys(engagement.timeCommitmentTypeKeys, SEAT_COMMITMENTS),
    advisorBio: seatAdvisorBio(engagement),
    hasAdvisor: seatHasAdvisor(engagement),
    engagementId: engagement.advisorEngagementId || undefined,
    statusKey: engagement.statusKey,
    invitedAdvisorName: advisorName,
    invitedAdvisorPictureUrl: engagement.advisorUser?.pictureUrl || null,
  }
}

export const buildSeatWhyText = (categoryRole: string | undefined, dos: Iterable<string>): string => {
  const dosList = [...dos]
  if (dosList.length === 0) {
    return categoryRole || ''
  }
  const bullets = dosList.map(item => `• ${item}`)
  return [categoryRole, ...bullets].filter(Boolean).join('\n\n')
}

/** Bullet asks stored by `buildSeatWhyText`, excluding the canned category role. */
export const seatAsksFromWhyText = (whyText: string | undefined | null): string[] => {
  if (!whyText) {
    return []
  }
  const items: string[] = []
  for (const line of whyText.split('\n')) {
    const trimmed = line.trim()
    if (!trimmed.startsWith('•')) {
      continue
    }
    const item = trimmed.replace(/^•\s*/, '').trim()
    if (item) {
      items.push(item)
    }
  }
  return items
}

export const isSeatCategoryRole = (text: string | undefined | null): boolean => {
  const trimmed = text?.trim()
  if (!trimmed) {
    return false
  }
  return SEAT_CATEGORIES.some(category => category.role === trimmed)
}

/** Why copy for display: selected asks only, never the canned category `role`. */
export const displayWhyTextWithoutCategoryRole = (whyText: string | undefined | null): string | null => {
  if (!whyText?.trim()) {
    return null
  }
  const asks = seatAsksFromWhyText(whyText)
  if (asks.length) {
    return asks.map(item => `• ${item}`).join('\n')
  }
  const trimmed = whyText.trim()
  if (isSeatCategoryRole(trimmed)) {
    return null
  }
  return trimmed
}

export const parseSeatDosFromWhyText = (whyText: string | undefined, knownDos: string[]): Set<string> => {
  const selected = new Set<string>()
  if (!whyText) {
    return selected
  }
  for (const item of knownDos) {
    if (whyText.includes(item)) {
      selected.add(item)
    }
  }
  // Custom asks are stored as "• …" bullets by `buildSeatWhyText`. Recover those
  // so an edit form can show tags that were typed in, not just the preset list.
  for (const item of seatAsksFromWhyText(whyText)) {
    selected.add(item)
  }
  return selected
}

/** Rehydrate commitment keys from structured notes or legacy free-text labels (ADV-M6). */
export const parseSeatCommitmentsFromNotes = (
  notes: string | undefined,
  knownCommitments: Array<string | { key: string; label: string }>
): Set<string> => {
  const selected = new Set<string>()
  if (!notes) {
    return selected
  }

  const options = knownCommitments.map(item => (typeof item === 'string' ? { key: item, label: item } : item))

  for (const line of notes
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(Boolean)) {
    const byKey = options.find(o => o.key === line)
    if (byKey) {
      selected.add(byKey.key)
      continue
    }
    const byLabel = options.find(o => o.label === line)
    if (byLabel) {
      selected.add(byLabel.key)
    }
  }

  // Fallback: substring match on full notes for legacy free-text seats
  if (selected.size === 0) {
    for (const o of options) {
      if (notes.includes(o.label) || notes.includes(o.key)) {
        selected.add(o.key)
      }
    }
  }
  return selected
}

export const formatSeatCommitmentNotes = (
  keys: Iterable<string>,
  knownCommitments: Array<{ key: string; label: string }>
): string => {
  const labels: string[] = []
  for (const key of keys) {
    const opt = knownCommitments.find(o => o.key === key)
    labels.push(opt?.label ?? key)
  }
  return labels.join('\n')
}
